// client/src/coms/workspace/workspaceModel.js
// Conversation tabs and the split view, as pure functions over a small serialisable state:
//   { v, tabs: [{ k: 'c', s: serverKey, c: channelId, p } | { k: 'd', t: threadId, p }], active, preview, split, closed }
// Tabs hold stable ids only (never names or message content); names come from live data when shown.
// The server stores the same shape (routes/comms/personalisation.js validates it).
//
// Smart tabs: browsing opens a conversation in the single *preview* tab (`preview` is its id), which the next
// conversation browsed to replaces, so clicking through 50 channels never fills the bar. A conversation the user
// writes in (or sends to, pins, keeps, opens in a new tab or in the split view) is promoted to a *saved* tab, which
// browsing never replaces. There is at most one preview tab, and every conversation has at most one tab.
import { LIMITS, sanitizeWorkspace, tabIdOf } from '../personalisation/prefsSchema';

export const EMPTY_WORKSPACE = Object.freeze({ v: 1, tabs: [], active: null, preview: null, split: { open: false, secondary: null, swapped: false }, closed: [] });

export { tabIdOf };

export function channelTab(serverKey, channelId, pinned = false) {
  return { k: 'c', s: String(serverKey), c: String(channelId), p: !!pinned };
}

export function dmTab(threadId, pinned = false) {
  return { k: 'd', t: String(threadId), p: !!pinned };
}

export function normalizeWorkspace(ws) {
  return sanitizeWorkspace(ws || {});
}

function findIndex(ws, id) {
  return ws.tabs.findIndex((t) => tabIdOf(t) === id);
}

// Pinned tabs stay together at the start, in their own order.
function orderPinned(tabs) {
  return [...tabs.filter((t) => t.p), ...tabs.filter((t) => !t.p)];
}

function fixSplit(ws) {
  const ids = new Set(ws.tabs.map(tabIdOf));
  const secondary = ws.split.secondary && ids.has(ws.split.secondary) && ws.split.secondary !== ws.active ? ws.split.secondary : null;
  return { ...ws, split: { ...ws.split, secondary, open: ws.split.open && !!secondary } };
}

// Full bar: the oldest unpinned tab that is neither active nor shown in the split nor the preview makes room.
function dropOldest(ws) {
  const victim = ws.tabs.findIndex((t) => !t.p && tabIdOf(t) !== ws.active && tabIdOf(t) !== ws.split.secondary && tabIdOf(t) !== ws.preview);
  if (victim < 0) return null;
  return ws.tabs.filter((_, i) => i !== victim);
}

function withClosed(ws, tab) {
  const id = tabIdOf(tab);
  const closed = [{ ...tab, p: false }, ...ws.closed.filter((t) => tabIdOf(t) !== id)].slice(0, LIMITS.closedTabs);
  return { ...ws, closed };
}

// Opens a conversation:
//  - already open (saved or preview) -> activate it; never a second tab. Opening it "to keep" (newTab, pinned)
//    also promotes a preview.
//  - browsing (default) -> it becomes the preview tab: the existing preview tab is replaced in place, otherwise a
//    preview tab is inserted after the active tab. Saved tabs are never replaced.
//  - opts.newTab (Ctrl/middle click, "open in new tab") or a pinned tab -> a new saved tab
//  - opts.activate false -> a background saved tab at the end
export function openTab(state, tab, { newTab = false, activate = true } = {}) {
  const ws = normalizeWorkspace(state);
  const id = tabIdOf(tab);
  if (!id) return ws;
  const keep = newTab || !activate || !!tab.p;
  const existing = findIndex(ws, id);
  if (existing >= 0) {
    let next = activate ? activateTab(ws, id) : ws;
    if (keep && next.preview === id) next = { ...next, preview: null };
    return next;
  }

  const fresh = { ...tab, p: !!tab.p };
  const asPreview = !keep;
  const previewIdx = ws.preview ? findIndex(ws, ws.preview) : -1;
  if (asPreview && previewIdx >= 0 && ws.split.secondary !== ws.preview) {
    const tabs = ws.tabs.map((t, i) => (i === previewIdx ? fresh : t));
    return fixSplit({ ...ws, tabs, preview: id, active: id });
  }

  let tabs = ws.tabs.length >= LIMITS.tabs ? dropOldest(ws) : [...ws.tabs];
  if (!tabs) return ws;
  if (!activate) {
    // Background tabs (Ctrl/middle click) queue up at the end in the order they were opened.
    tabs.push(fresh);
  } else {
    const activeAt = ws.active ? tabs.findIndex((t) => tabIdOf(t) === ws.active) : -1;
    tabs.splice(activeAt >= 0 ? activeAt + 1 : tabs.length, 0, fresh);
  }
  const next = { ...ws, tabs: orderPinned(tabs), preview: asPreview ? id : ws.preview && tabs.some((t) => tabIdOf(t) === ws.preview) ? ws.preview : null };
  return fixSplit(activate || !next.active ? { ...next, active: activate ? id : next.active || id } : next);
}

// The user engaged with a conversation (typed a non-empty draft, sent a message, chose "Keep tab open"): its
// preview tab becomes a saved tab. Anything else is unchanged; promoting twice is harmless.
export function promoteTab(state, id) {
  const ws = normalizeWorkspace(state);
  return ws.preview && ws.preview === id ? { ...ws, preview: null } : ws;
}

// The stored workspace arrived after this device already changed its own (e.g. the conversation opened on
// load). Keep both: every stored tab (with its pin), plus the tabs opened here; a tab is pinned if either copy
// pins it; what this device shows now stays active. Previously the stored copy was dropped, so opening Voxemera
// on another device lost pinned conversations and open tabs.
// Only one preview survives: this device's (what it is browsing now) wins; the stored preview tab is dropped
// unless this device saved it.
export function mergeWorkspace(stored, local) {
  const s = normalizeWorkspace(stored);
  const l = normalizeWorkspace(local);
  const localById = new Map(l.tabs.map((t) => [tabIdOf(t), t]));
  const dropStoredPreview = !!(s.preview && l.preview && s.preview !== l.preview && !localById.has(s.preview));
  const tabs = s.tabs
    .filter((t) => !(dropStoredPreview && tabIdOf(t) === s.preview))
    .map((t) => {
      const mine = localById.get(tabIdOf(t));
      return mine && mine.p && !t.p ? { ...t, p: true } : t;
    });
  const have = new Set(tabs.map(tabIdOf));
  for (const t of l.tabs) if (!have.has(tabIdOf(t))) tabs.push(t);
  const active = l.active || s.active;
  // A tab saved on either side stays saved.
  const savedSomewhere = (id) => (localById.has(id) && l.preview !== id) || (s.tabs.some((t) => tabIdOf(t) === id) && s.preview !== id);
  const preview = l.preview && !savedSomewhere(l.preview) ? l.preview : !l.preview && s.preview && !savedSomewhere(s.preview) ? s.preview : null;
  return normalizeWorkspace({ ...s, tabs: orderPinned(tabs), active, preview, split: l.split.open ? l.split : s.split });
}

export function activateTab(state, id) {
  const ws = normalizeWorkspace(state);
  if (findIndex(ws, id) < 0) return ws;
  // Activating the tab shown in the split swaps the two panes rather than showing it twice.
  if (ws.split.secondary === id && ws.active) return { ...ws, active: id, split: { ...ws.split, secondary: ws.active } };
  return fixSplit({ ...ws, active: id });
}

// Closing a tab never touches the conversation itself (messages, draft). A closed saved tab goes to the "reopen
// closed tab" list; a closed preview tab does not (it was never kept), and focus returns to the tab before it,
// which is the conversation that was open when browsing started.
export function closeTab(state, id) {
  const ws = normalizeWorkspace(state);
  const idx = findIndex(ws, id);
  if (idx < 0) return ws;
  const tab = ws.tabs[idx];
  const wasPreview = ws.preview === id;
  const tabs = ws.tabs.filter((_, i) => i !== idx);
  let active = ws.active;
  let split = ws.split;
  if (ws.split.secondary === id) split = { ...split, secondary: null, open: false };
  if (active === id) {
    if (split.secondary) {
      // Closing the main pane's tab: the split pane's conversation takes over.
      active = split.secondary;
      split = { ...split, secondary: null, open: false };
    } else {
      const neighbour = (wasPreview ? tabs[idx - 1] || tabs[idx] : tabs[idx] || tabs[idx - 1]) || null;
      active = neighbour ? tabIdOf(neighbour) : null;
    }
  }
  const next = { ...ws, tabs, active, split, preview: wasPreview ? null : ws.preview };
  return fixSplit(wasPreview ? next : withClosed(next, tab));
}

export function closeOtherTabs(state, id) {
  const ws = normalizeWorkspace(state);
  let next = ws;
  for (const t of ws.tabs) {
    const tid = tabIdOf(t);
    if (tid !== id && !t.p) next = closeTab(next, tid);
  }
  return activateTab(next, id);
}

export function reopenClosedTab(state) {
  const ws = normalizeWorkspace(state);
  const [last, ...rest] = ws.closed;
  if (!last) return ws;
  return openTab({ ...ws, closed: rest }, last, { newTab: true });
}

export function moveTab(state, id, toIndex) {
  const ws = normalizeWorkspace(state);
  const from = findIndex(ws, id);
  if (from < 0) return ws;
  const tab = ws.tabs[from];
  const rest = ws.tabs.filter((_, i) => i !== from);
  // Keep pinned and unpinned groups separate.
  const pinnedCount = rest.filter((t) => t.p).length;
  let to = Math.max(0, Math.min(rest.length, Number(toIndex) || 0));
  if (tab.p) to = Math.min(to, pinnedCount);
  else to = Math.max(to, pinnedCount);
  rest.splice(to, 0, tab);
  return { ...ws, tabs: rest };
}

export function moveTabBy(state, id, delta) {
  const ws = normalizeWorkspace(state);
  const from = findIndex(ws, id);
  if (from < 0) return ws;
  return moveTab(ws, id, from + delta);
}

// Pinning keeps a tab, so a pinned preview becomes saved.
export function setPinned(state, id, pinned) {
  const ws = normalizeWorkspace(state);
  const tabs = ws.tabs.map((t) => (tabIdOf(t) === id ? { ...t, p: !!pinned } : t));
  return { ...ws, tabs: orderPinned(tabs), preview: pinned && ws.preview === id ? null : ws.preview };
}

export function cycleTab(state, delta) {
  const ws = normalizeWorkspace(state);
  if (!ws.tabs.length) return ws;
  const idx = Math.max(0, findIndex(ws, ws.active));
  const next = ws.tabs[(idx + delta + ws.tabs.length) % ws.tabs.length];
  return activateTab(ws, tabIdOf(next));
}

// ---------- split view ----------
export function openSplit(state, secondaryId) {
  const ws = normalizeWorkspace(state);
  let id = secondaryId;
  if (!id || id === ws.active || findIndex(ws, id) < 0) {
    // Default: the most recently used other tab.
    const other = ws.tabs.find((t) => tabIdOf(t) !== ws.active);
    id = other ? tabIdOf(other) : null;
  }
  if (!id) return ws;
  // A conversation shown in the split pane is kept: browsing must not replace it.
  return { ...ws, preview: ws.preview === id ? null : ws.preview, split: { ...ws.split, open: true, secondary: id } };
}

// Shows a tab in the split pane even when it is the active tab (the main pane then needs another source,
// e.g. an activity); used by "chat beside activity".
export function setSecondary(state, id) {
  const ws0 = normalizeWorkspace(state);
  if (findIndex(ws0, id) < 0) return ws0;
  const ws = ws0.preview === id ? { ...ws0, preview: null } : ws0;
  if (id === ws.active) {
    const other = ws.tabs.find((t) => tabIdOf(t) !== id);
    return { ...ws, active: other ? tabIdOf(other) : ws.active, split: { ...ws.split, open: !!other, secondary: other ? id : null } };
  }
  return { ...ws, split: { ...ws.split, open: true, secondary: id } };
}

export function closeSplit(state) {
  const ws = normalizeWorkspace(state);
  return { ...ws, split: { ...ws.split, open: false } };
}

export function swapSplit(state) {
  const ws = normalizeWorkspace(state);
  if (!ws.split.open || !ws.split.secondary) return { ...ws, split: { ...ws.split, swapped: !ws.split.swapped } };
  return { ...ws, active: ws.split.secondary, split: { ...ws.split, secondary: ws.active } };
}

// ---------- validation against live data ----------
// isValid(tab) -> true | false. Invalid tabs (lost access, deleted channel or thread) are removed, and
// never shown from cache. Returns { ws, removed: [tab] }.
export function pruneTabs(state, isValid) {
  const ws = normalizeWorkspace(state);
  const removed = ws.tabs.filter((t) => !isValid(t));
  if (!removed.length) return { ws, removed };
  let next = ws;
  for (const t of removed) next = closeTab(next, tabIdOf(t));
  // Revoked conversations must not be offered by "reopen closed tab" either.
  next = { ...next, closed: next.closed.filter((t) => !removed.some((r) => tabIdOf(r) === tabIdOf(t))) };
  return { ws: next, removed };
}

export function tabsEqual(a, b) {
  return JSON.stringify(normalizeWorkspace(a)) === JSON.stringify(normalizeWorkspace(b));
}
